import React from 'react';
import { ClipboardList, LayoutDashboard, Sparkles } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PermissionCard, SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';
import { SmartFeaturesSection, type SmartFeatureItem } from './TimeTrackingSmartFeaturesSection';

export interface TimeTrackingTabProps {
  enabledPermissions: Set<string>;
  onTogglePermission: (permissionId: string, checked: boolean) => void;
  viewTimesheetsAccess: string;
  onViewTimesheetsAccessChange: (value: string) => void;
  editTimesheetsAccess: string;
  onEditTimesheetsAccessChange: (value: string) => void;
  allowDeleteTimesheets: boolean;
  onAllowDeleteTimesheetsChange: (value: boolean) => void;
  allowDuplicateTimesheets: boolean;
  onAllowDuplicateTimesheetsChange: (value: boolean) => void;
  allowRecallTimesheet: boolean;
  onAllowRecallTimesheetChange: (value: boolean) => void;
  smartFeatureItems: SmartFeatureItem[];
  onSmartFeatureChange: (item: SmartFeatureItem, value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

const homepagePermissions = [
  {
    id: 'dashboard.view_own',
    label: 'View Personal Homepage',
    description: 'Access to personal homepage with dashboard and metrics',
    disabled: false,
  },
  {
    id: 'dashboard.view_team',
    label: 'View Team Dashboard',
    description: 'Access to team dashboard with team member metrics',
    disabled: true,
  },
  {
    id: 'dashboard.view_department',
    label: 'View Department Dashboard',
    description: 'Access to department-wide dashboard with aggregated metrics',
    disabled: true,
  },
];

export const TimeTrackingTab: React.FC<TimeTrackingTabProps> = ({
  enabledPermissions,
  onTogglePermission,
  viewTimesheetsAccess,
  onViewTimesheetsAccessChange,
  editTimesheetsAccess,
  onEditTimesheetsAccessChange,
  allowDeleteTimesheets,
  onAllowDeleteTimesheetsChange,
  allowDuplicateTimesheets,
  onAllowDuplicateTimesheetsChange,
  allowRecallTimesheet,
  onAllowRecallTimesheetChange,
  smartFeatureItems,
  onSmartFeatureChange,
  onSelectAll,
  onDeselectAll,
}) => (
  <>
    <BulkActionsDropdown onSelectAll={onSelectAll} onDeselectAll={onDeselectAll} />
    <Accordion type="multiple" defaultValue={['personal-homepage', 'timesheet-access', 'smart-features']} className="space-y-3">

      <SectionAccordionItem value="personal-homepage" icon={<LayoutDashboard className="h-5 w-5" />} title="Personal Homepage" description="Access to personal homepage and metrics">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {homepagePermissions.map(({ id, label, description, disabled }) => (
            <PermissionCard
              key={id}
              label={<Label htmlFor={id} className={disabled ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}>{label}</Label>}
              description={description}
              dimmed={disabled || undefined}
              control={
                <Checkbox
                  id={id}
                  checked={enabledPermissions.has(id)}
                  disabled={disabled || undefined}
                  onCheckedChange={(checked) => onTogglePermission(id, !!checked)}
                />
              }
            />
          ))}
        </div>
      </SectionAccordionItem>

      <SectionAccordionItem value="timesheet-access" icon={<ClipboardList className="h-5 w-5" />} title="Timesheet Access" description="View, edit, and delete timesheet permissions">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PermissionCard
            label={<Label htmlFor="view-timesheets">View timesheets</Label>}
            description="Access level for viewing timesheet entries"
            control={
              <Select value={viewTimesheetsAccess} onValueChange={onViewTimesheetsAccessChange}>
                <SelectTrigger id="view-timesheets" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="own">Own only</SelectItem>
                  <SelectItem value="team">Team (department)</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            }
          />
          <PermissionCard
            label={<Label htmlFor="edit-timesheets">Edit timesheets</Label>}
            description="Access level for modifying timesheet entries"
            control={
              <Select value={editTimesheetsAccess} onValueChange={onEditTimesheetsAccessChange}>
                <SelectTrigger id="edit-timesheets" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="own">Own only</SelectItem>
                  <SelectItem value="team">Team (department)</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            }
          />
          <PermissionCard
            label={<Label htmlFor="allow-delete" className="cursor-pointer">Allow Delete timesheets</Label>}
            description="Allow deleting of timesheet lines"
            onToggle={() => onAllowDeleteTimesheetsChange(!allowDeleteTimesheets)}
            control={<Checkbox id="allow-delete" checked={allowDeleteTimesheets} onCheckedChange={(c) => onAllowDeleteTimesheetsChange(c as boolean)} />}
          />
          <PermissionCard
            label={<Label htmlFor="allow-duplicate" className="cursor-pointer">Allow Duplicate timesheet lines</Label>}
            description="Allow duplicate of timesheet lines"
            onToggle={() => onAllowDuplicateTimesheetsChange(!allowDuplicateTimesheets)}
            control={<Checkbox id="allow-duplicate" checked={allowDuplicateTimesheets} onCheckedChange={(c) => onAllowDuplicateTimesheetsChange(c as boolean)} />}
          />
          <PermissionCard
            label={<Label htmlFor="allow-recall" className="cursor-pointer">Recall Timesheet</Label>}
            description="Allow recalling submitted timesheets for corrections"
            onToggle={() => onAllowRecallTimesheetChange(!allowRecallTimesheet)}
            control={<Checkbox id="allow-recall" checked={allowRecallTimesheet} onCheckedChange={(c) => onAllowRecallTimesheetChange(c as boolean)} />}
          />
        </div>
      </SectionAccordionItem>

      <SectionAccordionItem value="smart-features" icon={<Sparkles className="h-5 w-5" />} title="Smart Features Permissions" description="Control access to intelligent time entry features">
        <TooltipProvider>
          <SmartFeaturesSection items={smartFeatureItems} onChange={onSmartFeatureChange} />
        </TooltipProvider>
      </SectionAccordionItem>
    </Accordion>
  </>
);
